import { supabase } from '@/lib/supabase/client'

export type ChatbotLog = {
  id: string
  session_id: string
  user_message: string | null
  bot_response: string | null
  step_id: string
  is_unanswered: boolean
  created_at: string
}

export type ChatbotLogInsert = Omit<ChatbotLog, 'id' | 'created_at'>

export const logChatInteraction = async (log: ChatbotLogInsert) => {
  const { error } = await supabase.from('chatbot_logs').insert([log])
  return { error }
}

export const getChatbotLogs = async () => {
  const { data, error } = await supabase
    .from('chatbot_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(1000)

  return { data: data as ChatbotLog[], error }
}

export const getUnansweredQuestions = async () => {
  const { data, error } = await supabase
    .from('chatbot_logs')
    .select('*')
    .eq('is_unanswered', true)
    .not('user_message', 'is', null)
    .order('created_at', { ascending: false })

  return { data: data as ChatbotLog[], error }
}

export const getDropOffAnalysis = async () => {
  const { data, error } = await supabase
    .from('chatbot_logs')
    .select('step_id, session_id')

  if (error || !data) return { data: [], error }

  // Process data to count unique sessions per step
  const stepCounts: Record<string, Set<string>> = {}

  data.forEach((log) => {
    if (log.step_id) {
      if (!stepCounts[log.step_id]) {
        stepCounts[log.step_id] = new Set()
      }
      stepCounts[log.step_id].add(log.session_id)
    }
  })

  // Order steps logically for the funnel
  const orderedSteps = [
    'INIT',
    'MATURITY',
    'DIMENSION',
    'HOURS',
    'PAIN',
    'SECTOR',
    'IMPACT',
    'CAPTURE_NAME',
    'CAPTURE_COMPANY',
    'CAPTURE_EMAIL',
    'CAPTURE_PHONE',
    'CONSENT',
    'SUBMITTING',
  ]

  const chartData = orderedSteps
    .map((step) => ({
      step,
      count: stepCounts[step]?.size || 0,
    }))
    .filter((item) => item.count > 0) // Filter out steps with 0 to clean up chart if empty

  return { data: chartData, error: null }
}
